import { Logger } from '@/utils/Logger';
import { shouldShowFeatureToggles } from '@/dev/utils/environment';
import { FEATURE_NAMES, isFeatureName, overrideKey } from './FeatureToggleContext';

const moduleName = 'FeatureToggle';

export const URL_FEATURE_PARAM = 'ff';

/**
 * Removes the `?ff=` param from the current URL via history.replaceState, leaving the
 * applied overrides intact in localStorage. No-op outside the browser or when absent.
 */
export const stripFeatureParamFromUrl = (): void => {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;

  const url = new URL(window.location.href);
  if (!url.searchParams.has(URL_FEATURE_PARAM)) return;

  url.searchParams.delete(URL_FEATURE_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

/**
 * Applies feature overrides supplied through the `?ff=` query param so a flagged
 * view can be shared via a link, e.g. `?ff=showAllSegmentationMetrixTab`.
 * Each comma-separated token is `name` (enabled) or `name:false` (disabled). The
 * resolved value is written to the same localStorage override the dev panel uses,
 * so the change persists and is reflected by the panel.
 *
 * Gated by `shouldShowFeatureToggles()`, so it is a no-op on staging/production.
 */
export const applyUrlFeatureOverrides = (search: string): void => {
  if (!shouldShowFeatureToggles()) return;

  const rawValue = new URLSearchParams(search).get(URL_FEATURE_PARAM);
  if (!rawValue) return;

  rawValue
    .split(',')
    .map(token => token.trim())
    .filter(Boolean)
    .forEach(token => {
      const [name, value] = token.split(':');
      if (!isFeatureName(name)) {
        Logger.warn(moduleName, `Ignoring unknown feature "${name}" from the "${URL_FEATURE_PARAM}" URL param.`);
        return;
      }
      localStorage.setItem(overrideKey(name), value === 'false' ? 'false' : 'true');
    });

  // Overrides now live in localStorage, so drop the param to keep the URL clean and avoid
  // re-applying a stale link on later navigations/bookmarks.
  stripFeatureParamFromUrl();
};

/**
 * Serialises the currently active localStorage overrides into a `?ff=` value
 * (the inverse of {@link applyUrlFeatureOverrides}). Enabled flags are emitted as
 * `name`, disabled ones as `name:false`. Returns an empty string when nothing is
 * overridden.
 */
export const buildFeatureOverrideParam = (): string =>
  FEATURE_NAMES.filter(name => localStorage.getItem(overrideKey(name)) !== null)
    .map(name => (localStorage.getItem(overrideKey(name)) === 'true' ? name : `${name}:false`))
    .join(',');

/**
 * Builds a shareable URL that reproduces the current overrides. Returns an empty
 * string when there is nothing to share.
 */
export const buildShareUrl = (location: { origin: string; pathname: string }): string => {
  const param = buildFeatureOverrideParam();
  return param ? `${location.origin}${location.pathname}?${URL_FEATURE_PARAM}=${param}` : '';
};
